import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Clock, Wind, Sunset, Mountain, Shuffle, X, Loader2, Info } from 'lucide-react'
import * as Switch from '@radix-ui/react-switch'
import { useRoadStore } from '@/lib/roads/store'
import {
  generateRide,
  type GeneratedRide,
  type RideLength,
  type RideStyle,
} from '@/lib/roads/ride'
import BikeTypeTip from '@/components/ui/BikeTypeTip'

interface FindRideModalProps {
  open: boolean
  onClose: () => void
}

const LENGTHS: { id: RideLength; label: string; hint: string }[] = [
  { id: '1h', label: '1 hour', hint: '~35 mi' },
  { id: '2h', label: '2 hours', hint: '~70 mi' },
  { id: 'half', label: 'Half day', hint: '~150 mi' },
  { id: 'full', label: 'Full day', hint: '~250 mi' },
]

const STYLES: { id: RideStyle; label: string; sub: string; icon: typeof Wind }[] = [
  { id: 'twisty', label: 'Twisty', sub: 'Sport mountain', icon: Wind },
  { id: 'scenic', label: 'Scenic', sub: 'Relaxed cruise', icon: Sunset },
  { id: 'adventure', label: 'Adventure', sub: 'Dual-sport', icon: Mountain },
  { id: 'mixed', label: 'Mixed bag', sub: 'A bit of everything', icon: Shuffle },
]

const BIKE_DOTS: Record<string, string> = {
  Sport: '#FF4438',
  ADV: '#34D26B',
  Sumo: '#FF9F1C',
  Cruiser: '#3B82F6',
}

export default function FindRideModal({ open, onClose }: FindRideModalProps) {
  const roads = useRoadStore((s) => s.roads)
  const loading = useRoadStore((s) => s.loading)
  const startRide = useRoadStore((s) => s.startRide)

  const [step, setStep] = useState<'choices' | 'result'>('choices')
  const [length, setLength] = useState<RideLength>('2h')
  const [style, setStyle] = useState<RideStyle>('twisty')
  const [avoidFreeways, setAvoidFreeways] = useState(true)
  const [seed, setSeed] = useState(1)
  const [result, setResult] = useState<GeneratedRide | null>(null)

  useEffect(() => {
    if (open) {
      setStep('choices')
      setSeed(1)
      setResult(null)
    }
  }, [open])

  const find = (s: number) => {
    setResult(generateRide(roads, { length, style, avoidFreeways, seed: s }))
    setStep('result')
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 60 }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="fixed z-50 bg-[#0C0C0E] border border-white/10 rounded-t-2xl md:rounded-xl
              inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto
              md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[28rem]"
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="text-xs font-mono uppercase tracking-widest text-amber-400">
                Pick a route for me
              </div>
              <button
                onClick={onClose}
                className="h-11 w-11 -mr-2 flex items-center justify-center rounded-lg text-white/50 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {loading && roads.length === 0 && (
                <div className="flex flex-col items-center gap-3 py-10">
                  <Loader2 className="animate-spin text-amber-400" size={28} />
                  <span className="text-sm text-white/50">Loading curated roads…</span>
                </div>
              )}

              {!loading && roads.length === 0 && (
                <div className="py-10 text-center text-sm text-white/50">
                  No curated roads match yet — the founder is still riding. Try different criteria.
                </div>
              )}

              {roads.length > 0 && step === 'choices' && (
                <>
                  <div className="space-y-2">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                      Ride length
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {LENGTHS.map((l) => (
                        <button
                          key={l.id}
                          onClick={() => setLength(l.id)}
                          className={`min-h-14 rounded-lg border px-3 text-left flex items-center gap-2.5 transition-colors ${
                            length === l.id
                              ? 'bg-amber-400/15 border-amber-400/60 text-amber-200'
                              : 'bg-white/[0.03] border-white/10 text-white/70 hover:border-white/25'
                          }`}
                        >
                          <Clock size={16} className="shrink-0" />
                          <span className="leading-tight">
                            <span className="block text-sm font-medium">{l.label}</span>
                            <span className="block text-[10px] text-white/40">{l.hint}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                      Ride style
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {STYLES.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => setStyle(s.id)}
                          className={`min-h-14 rounded-lg border px-3 text-left flex items-center gap-2.5 transition-colors ${
                            style === s.id
                              ? 'bg-amber-400/15 border-amber-400/60 text-amber-200'
                              : 'bg-white/[0.03] border-white/10 text-white/70 hover:border-white/25'
                          }`}
                        >
                          <s.icon size={16} className="shrink-0" />
                          <span className="leading-tight">
                            <span className="block text-sm font-medium">{s.label}</span>
                            <span className="block text-[10px] text-white/40">{s.sub}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2.5">
                    <div className="text-sm text-white/80">Start: San Francisco</div>
                    <div className="text-[11px] text-white/35 flex items-center gap-1 mt-0.5">
                      <Info size={11} /> uses your map area in the future
                    </div>
                  </div>

                  <label className="flex items-center justify-between min-h-11">
                    <span className="text-sm text-white/75">Avoid freeways</span>
                    <Switch.Root
                      checked={avoidFreeways}
                      onCheckedChange={setAvoidFreeways}
                      className="w-10 h-6 rounded-full bg-white/15 data-[state=checked]:bg-amber-400 relative transition-colors"
                    >
                      <Switch.Thumb className="block w-4.5 h-4.5 w-[18px] h-[18px] rounded-full bg-white translate-x-1 data-[state=checked]:translate-x-[22px] transition-transform" />
                    </Switch.Root>
                  </label>

                  <button
                    onClick={() => find(seed)}
                    className="min-h-12 w-full rounded-lg bg-amber-400 hover:bg-amber-300 text-black font-bold text-sm tracking-widest"
                  >
                    FIND MY RIDE
                  </button>
                </>
              )}

              {roads.length > 0 && step === 'result' && result && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-lg bg-white/[0.04] border border-white/10 py-2.5">
                      <div className="text-lg font-bold text-amber-400">{result.totalMiles}</div>
                      <div className="text-[10px] font-mono uppercase text-white/40">miles</div>
                    </div>
                    <div className="rounded-lg bg-white/[0.04] border border-white/10 py-2.5">
                      <div className="text-lg font-bold text-white">{result.estHours}h</div>
                      <div className="text-[10px] font-mono uppercase text-white/40">est. time</div>
                    </div>
                    <div className="rounded-lg bg-white/[0.04] border border-white/10 py-2.5">
                      <div className="text-lg font-bold text-white">{result.roads.length}</div>
                      <div className="text-[10px] font-mono uppercase text-white/40">roads</div>
                    </div>
                  </div>
                  <div className="text-center text-[10px] font-mono uppercase tracking-widest text-white/40">
                    Avg rating{' '}
                    <span className="text-amber-400 font-bold">{result.avgRating.toFixed(1)}</span>{' '}
                    / 10
                  </div>

                  <div className="space-y-1.5">
                    {result.roads.map((r, i) => (
                      <motion.div
                        key={r.id}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.06 }}
                        className="flex items-center gap-2 rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2.5"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white truncate">{r.name}</div>
                          {r.distance_miles != null && (
                            <div className="text-[10px] text-white/40">{r.distance_miles} mi</div>
                          )}
                        </div>
                        <span className="px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 text-xs font-mono">
                          {(r.ratings?.overall ?? 0).toFixed(1)}
                        </span>
                        <div className="flex flex-col items-end gap-0.5">
                          {r.bike_types.map((b) => (
                            <BikeTypeTip key={b} type={b}>
                              <span
                                className="text-[10px] font-mono uppercase text-white/60 cursor-default"
                              >
                                {b}
                                <span
                                  className="block w-full h-[2px] mt-0.5 rounded-full"
                                  style={{ background: BIKE_DOTS[b] ?? '#888' }}
                                />
                              </span>
                            </BikeTypeTip>
                          ))}
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const next = seed + 1
                        setSeed(next)
                        find(next)
                      }}
                      className="min-h-12 flex-1 rounded-lg border border-white/20 text-white/85 text-sm font-medium flex items-center justify-center gap-2"
                    >
                      <Shuffle size={15} /> SHOW ME ANOTHER
                    </button>
                    <button
                      onClick={() => {
                        startRide('route', result.roadIds)
                        onClose()
                      }}
                      className="min-h-12 flex-1 rounded-lg bg-amber-400 hover:bg-amber-300 text-black font-bold text-sm tracking-wide"
                    >
                      START RIDE
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
